import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { apiDelete, apiGet, apiPatch, apiPost, TENANT_TOKEN_KEY } from '../../api/client';
import { useTenantPermissions } from '../../auth/TenantPermissionsContext';
import { showGrowl } from '../../ui/growlState';
import { DemoMessageLimitsSection } from './DemoMessageLimitsSection';

type Agent = {
  id: string;
  name: string;
  systemPrompt: string | null;
  model: string;
  useRag: boolean;
  isActive: boolean;
  dailyMessageLimit?: number | null;
  extensionAmount?: number | null;
  extensionValidityDays?: number | null;
  audioOutputEnabled?: boolean;
  audioInputEnabled?: boolean;
  elevenLabsApiKeyConfigured?: boolean;
  elevenLabsApiKeyHint?: string | null;
  elevenLabsVoiceId?: string | null;
  elevenLabsModelId?: string | null;
  showWhatsappCustomerWindow?: boolean;
};

type ContextDoc = {
  id: string;
  originalFileName: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
};

type ContextUrlRow = {
  id: string;
  url: string;
  lastFetchedAt: string | null;
  fetchError: string | null;
  textChars: number;
  hasContent: boolean;
};

type ModelPreset = { id: string; label: string };

type AgentModalState = { kind: 'create' } | { kind: 'edit'; agent: Agent };

const TABLE_PAGE_SIZE = 10;

/** Iconos de acciones de tabla (stroke, hereda `currentColor`). */
function IconPencil() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  );
}

function IconPause() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </svg>
  );
}

function IconPlay() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M8 5v14l11-7-11-7z" />
    </svg>
  );
}

function IconFileStack() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

function IconPlugins() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  );
}

const FALLBACK_MODEL_PRESETS: ModelPreset[] = [
  { id: 'gpt-4o', label: 'GPT-4o' },
  { id: 'gpt-4o-mini', label: 'GPT-4o mini (recomendado OpenAI)' },
  { id: 'gpt-4.1', label: 'GPT-4.1' },
  { id: 'gpt-4.1-mini', label: 'GPT-4.1 mini' },
  { id: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
  { id: 'o1', label: 'o1' },
  { id: 'o1-mini', label: 'o1 mini' },
  { id: 'o3-mini', label: 'o3 mini' },
  { id: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
  { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
  { id: 'claude-opus-4-7', label: 'Claude Opus 4.7' },
  { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
  { id: 'claude-haiku-5-5', label: 'Claude Haiku 5.5' },
  { id: 'deepseek-chat', label: 'DeepSeek Chat' },
  { id: 'deepseek-reasoner', label: 'DeepSeek Reasoner' },
];

function effectiveModelFromForm(
  useCustom: boolean,
  presetId: string,
  customText: string
): string {
  if (useCustom) return customText.trim();
  return presetId.trim() || 'gpt-4o-mini';
}

/** RAG (embeds OpenAI) se ofrece solo con modelos de chat de la API de OpenAI, no con Claude/DeepSeek. */
function isOpenAiChatModelId(model: string | null | undefined): boolean {
  const m = (model ?? '').trim().toLowerCase();
  if (!m) return false;
  if (m.startsWith('claude-') || m.startsWith('deepseek-')) return false;
  if (m.startsWith('gpt-') || m.startsWith('chatgpt-')) return true;
  if (/^o[0-4]/.test(m)) return true; // o1, o1-mini, o3-mini, o4-mini, etc.
  return false;
}

type ModelPickerProps = {
  presets: ModelPreset[];
  useCustom: boolean;
  onUseCustomChange: (v: boolean) => void;
  presetId: string;
  onPresetIdChange: (id: string) => void;
  customText: string;
  onCustomTextChange: (s: string) => void;
};

function ModelPicker({
  presets,
  useCustom,
  onUseCustomChange,
  presetId,
  onPresetIdChange,
  customText,
  onCustomTextChange,
}: ModelPickerProps) {
  const [open, setOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    if (!q) return presets;
    return presets.filter(
      (p) => p.id.toLowerCase().includes(q) || p.label.toLowerCase().includes(q)
    );
  }, [presets, filterQuery]);

  const selectedLabel = presets.find((p) => p.id === presetId)?.label ?? presetId;
  const qTrim = filterQuery.trim();
  const presetIdExactMatch = presets.some((p) => p.id === qTrim);
  const showUseTypedAsApiId = open && qTrim.length > 0 && !presetIdExactMatch;

  const inputValue = open ? filterQuery : useCustom ? customText : selectedLabel;

  const pickPreset = (p: ModelPreset) => {
    onPresetIdChange(p.id);
    onUseCustomChange(false);
    setOpen(false);
    setFilterQuery('');
  };

  const pickCustomId = (id: string) => {
    onUseCustomChange(true);
    onCustomTextChange(id);
    setOpen(false);
    setFilterQuery('');
  };

  const listboxStyle: CSSProperties = {
    position: 'absolute',
    zIndex: 20,
    left: 0,
    right: 0,
    top: '100%',
    margin: '4px 0 0',
    padding: 4,
    listStyle: 'none',
    maxHeight: 260,
    overflowY: 'auto',
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    boxShadow: '0 12px 24px rgba(15,23,42,0.12)',
  };

  const optionBtn = (active: boolean): CSSProperties => ({
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '8px 10px',
    border: 'none',
    borderRadius: 6,
    background: active ? '#f5f3ff' : 'transparent',
    cursor: 'pointer',
    fontSize: 13,
    color: '#0f172a',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={lab}>
        Modelo
        <div ref={wrapRef} style={{ position: 'relative' }}>
          <input
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            style={{ ...inp, fontFamily: useCustom && !open ? 'ui-monospace, monospace' : undefined }}
            value={inputValue}
            placeholder="Buscar en la lista o escribir un ID para la API…"
            autoComplete="off"
            onFocus={() => {
              setOpen(true);
              setFilterQuery(useCustom ? customText : '');
            }}
            onChange={(e) => {
              const v = e.target.value;
              setOpen(true);
              setFilterQuery(v);
              if (useCustom) onCustomTextChange(v);
            }}
          />
          {open ? (
            <ul role="listbox" style={listboxStyle}>
              {filtered.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    role="option"
                    style={optionBtn(!useCustom && p.id === presetId)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickPreset(p)}
                  >
                    <strong style={{ fontWeight: 600 }}>{p.label}</strong>
                    <span style={{ color: '#64748b', marginLeft: 8, fontFamily: 'ui-monospace, monospace' }}>
                      {p.id}
                    </span>
                  </button>
                </li>
              ))}
              {filtered.length === 0 && !showUseTypedAsApiId ? (
                <li style={{ padding: '8px 10px', fontSize: 13, color: '#94a3b8' }}>
                  Sin coincidencias en la lista. Podés usar un ID propio abajo.
                </li>
              ) : null}
              {showUseTypedAsApiId ? (
                <li style={{ borderTop: '1px solid #f1f5f9', marginTop: 2, paddingTop: 2 }}>
                  <button
                    type="button"
                    role="option"
                    style={optionBtn(useCustom && customText.trim() === qTrim)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickCustomId(qTrim)}
                  >
                    <span style={{ fontWeight: 600 }}>Agregar vía API</span>
                    <span style={{ color: '#64748b', marginLeft: 8, fontFamily: 'ui-monospace, monospace' }}>
                      {qTrim}
                    </span>
                    <span style={{ display: 'block', fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                      Se envía en POST/PATCH como <code style={{ fontSize: 11 }}>model</code>
                    </span>
                  </button>
                </li>
              ) : null}
              <li style={{ borderTop: '1px solid #f1f5f9', marginTop: 2, paddingTop: 2 }}>
                <button
                  type="button"
                  role="option"
                  style={optionBtn(false)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickCustomId(qTrim)}
                >
                  <span style={{ fontWeight: 600 }}>Modelo propio (solo ID para la API)…</span>
                  <span style={{ display: 'block', fontSize: 11, color: '#64748b', marginTop: 2 }}>
                    Escribí el ID arriba y elegí esta fila, o dejá vacío y completalo después. Mismo campo{' '}
                    <code style={{ fontSize: 11 }}>model</code> en la API.
                  </span>
                </button>
              </li>
            </ul>
          ) : null}
        </div>
      </label>
      <p style={{ margin: 0, fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
        Lista de modelos y claves de API: las define el <strong>super-admin</strong> en{' '}
        <code>/super/ai</code> (cifrado en base de datos). Modelos <strong>claude-*</strong> requieren clave
        Anthropic; el resto, OpenAI. Variables de entorno siguen sirviendo como respaldo si no hay clave en BD.
      </p>
    </div>
  );
}

export function AgentsPage() {
  const token = localStorage.getItem(TENANT_TOKEN_KEY)!;
  const { can } = useTenantPermissions();
  const canWriteAgents = can('web.agents.write');
  const canManageAgents = can('web.agents.manage');
  const [rows, setRows] = useState<Agent[]>([]);
  const [presets, setPresets] = useState<ModelPreset[]>(FALLBACK_MODEL_PRESETS);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState('');
  const [agentQuery, setAgentQuery] = useState('');
  const [tablePage, setTablePage] = useState(1);
  const [agentModal, setAgentModal] = useState<AgentModalState | null>(null);
  const [modalBusy, setModalBusy] = useState(false);
  const [modalName, setModalName] = useState('');
  const [modalPrompt, setModalPrompt] = useState('');
  const [modalUseCustom, setModalUseCustom] = useState(false);
  const [modalPresetId, setModalPresetId] = useState('gpt-4o-mini');
  const [modalCustomModel, setModalCustomModel] = useState('');
  const [modalUseRag, setModalUseRag] = useState(false);
  const [docsAgentId, setDocsAgentId] = useState<string | null>(null);
  const [contextDocs, setContextDocs] = useState<ContextDoc[]>([]);
  const [contextUrls, setContextUrls] = useState<ContextUrlRow[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlBusy, setUrlBusy] = useState(false);
  const [urlRefreshingId, setUrlRefreshingId] = useState<string | null>(null);

  const [modalMessageLimit, setModalMessageLimit] = useState<number | null>(null);
  const [modalValidityDays, setModalValidityDays] = useState<number | null>(null);
  const [modalExtensionAmount, setModalExtensionAmount] = useState<number | null>(null);
  const [modalLimitErrors, setModalLimitErrors] = useState<Record<string, string>>({});
  const [modalAudioOutputEnabled, setModalAudioOutputEnabled] = useState(false);
  const [modalAudioInputEnabled, setModalAudioInputEnabled] = useState(false);
  const [modalShowWhatsappWindow, setModalShowWhatsappWindow] = useState(false);
  const [modalElevenLabsApiKey, setModalElevenLabsApiKey] = useState('');
  const [modalElevenLabsVoiceId, setModalElevenLabsVoiceId] = useState('');
  const [modalElevenLabsModelId, setModalElevenLabsModelId] = useState('');
  const [modalElevenLabsKeyConfigured, setModalElevenLabsKeyConfigured] = useState(false);
  const [modalElevenLabsKeyHint, setModalElevenLabsKeyHint] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await apiGet<Agent[]>('/api/console/agents', token);
      setRows(data);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [token]);

  const loadPresets = useCallback(async () => {
    try {
      const data = await apiGet<ModelPreset[]>('/api/console/ai-model-presets', token);
      if (Array.isArray(data) && data.length > 0) setPresets(data);
    } catch {
      setPresets(FALLBACK_MODEL_PRESETS);
    }
  }, [token]);

  const loadContextPanel = useCallback(
    async (agentId: string) => {
      setDocsLoading(true);
      setErr('');
      try {
        const [docs, urls] = await Promise.all([
          apiGet<ContextDoc[]>(`/api/console/agents/${agentId}/context-documents`, token),
          apiGet<ContextUrlRow[]>(`/api/console/agents/${agentId}/context-urls`, token),
        ]);
        setContextDocs(docs);
        setContextUrls(urls);
      } catch (e) {
        setErr((e as Error).message);
        setContextDocs([]);
        setContextUrls([]);
      } finally {
        setDocsLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    void load();
    void loadPresets();
  }, [load, loadPresets]);

  useEffect(() => {
    if (docsAgentId) {
      setUrlInput('');
      void loadContextPanel(docsAgentId);
    } else {
      setContextDocs([]);
      setContextUrls([]);
    }
  }, [docsAgentId, loadContextPanel]);

  useEffect(() => {
    if (!agentModal) return;
    const defaultPreset = presets.find((p) => p.id === 'gpt-4o-mini')?.id ?? presets[0]?.id ?? 'gpt-4o-mini';
    if (agentModal.kind === 'create') {
      setModalName('');
      setModalPrompt('');
      setModalUseCustom(false);
      setModalPresetId(defaultPreset);
      setModalCustomModel('');
      setModalUseRag(false);
      setModalMessageLimit(null);
      setModalValidityDays(null);
      setModalExtensionAmount(null);
      setModalLimitErrors({});
      setModalAudioOutputEnabled(false);
      setModalAudioInputEnabled(false);
      setModalShowWhatsappWindow(false);
      setModalElevenLabsApiKey('');
      setModalElevenLabsVoiceId('');
      setModalElevenLabsModelId('');
      setModalElevenLabsKeyConfigured(false);
      setModalElevenLabsKeyHint(null);
    } else {
      const a = agentModal.agent;
      setModalName(a.name);
      setModalPrompt(a.systemPrompt ?? '');
      setModalUseRag(Boolean(a.useRag) && isOpenAiChatModelId(a.model));
      const hit = presets.find((p) => p.id === a.model);
      if (hit) {
        setModalUseCustom(false);
        setModalPresetId(a.model);
        setModalCustomModel('');
      } else {
        setModalUseCustom(true);
        setModalCustomModel(a.model);
        setModalPresetId(defaultPreset);
      }
      setModalMessageLimit(a.dailyMessageLimit ?? null);
      setModalValidityDays(a.extensionValidityDays ?? null);
      setModalExtensionAmount(a.extensionAmount ?? null);
      setModalLimitErrors({});
      setModalAudioOutputEnabled(Boolean(a.audioOutputEnabled));
      setModalAudioInputEnabled(Boolean(a.audioInputEnabled));
      setModalShowWhatsappWindow(Boolean(a.showWhatsappCustomerWindow));
      setModalElevenLabsApiKey('');
      setModalElevenLabsVoiceId(a.elevenLabsVoiceId ?? '');
      setModalElevenLabsModelId(a.elevenLabsModelId ?? '');
      setModalElevenLabsKeyConfigured(Boolean(a.elevenLabsApiKeyConfigured));
      setModalElevenLabsKeyHint(a.elevenLabsApiKeyHint ?? null);
    }
  }, [agentModal, presets]);

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => a.name.localeCompare(b.name)),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const q = agentQuery.trim().toLowerCase();
    if (!q) return sortedRows;
    return sortedRows.filter((a) => {
      const parts = [a.name, a.systemPrompt ?? '', a.id, a.isActive ? 'activo' : 'pausado'];
      if (canWriteAgents) parts.push(a.model);
      return parts.join(' ').toLowerCase().includes(q);
    });
  }, [sortedRows, agentQuery, canWriteAgents]);

  const tableTotalPages = Math.max(1, Math.ceil(filteredRows.length / TABLE_PAGE_SIZE));
  const tableSlice = useMemo(() => {
    const start = (tablePage - 1) * TABLE_PAGE_SIZE;
    return filteredRows.slice(start, start + TABLE_PAGE_SIZE);
  }, [filteredRows, tablePage]);

  useEffect(() => {
    if (tablePage > tableTotalPages) setTablePage(tableTotalPages);
  }, [tablePage, tableTotalPages]);

  useEffect(() => {
    setTablePage(1);
  }, [agentQuery]);

  const modalEffectiveModel = useMemo(
    () => effectiveModelFromForm(modalUseCustom, modalPresetId, modalCustomModel),
    [modalUseCustom, modalPresetId, modalCustomModel]
  );
  const showRagOption = useMemo(() => isOpenAiChatModelId(modalEffectiveModel), [modalEffectiveModel]);

  useEffect(() => {
    if (!showRagOption && modalUseRag) setModalUseRag(false);
  }, [showRagOption, modalUseRag]);

  function handleLimitChange(field: string, value: number | null) {
    const ranges: Record<string, { min: number; max: number }> = {
      messageLimit: { min: 1, max: 10000 },
      validityDays: { min: 1, max: 365 },
      extensionAmount: { min: 1, max: 10000 },
    };

    if (field === 'messageLimit') setModalMessageLimit(value);
    else if (field === 'validityDays') setModalValidityDays(value);
    else if (field === 'extensionAmount') setModalExtensionAmount(value);

    setModalLimitErrors((prev) => {
      const next = { ...prev };
      if (value === null) {
        delete next[field];
      } else {
        const range = ranges[field];
        if (range && (!Number.isInteger(value) || value < range.min || value > range.max)) {
          next[field] = `Debe ser un entero entre ${range.min} y ${range.max}`;
        } else {
          delete next[field];
        }
      }
      return next;
    });
  }

  const submitModal = async (e: React.FormEvent) => {
    e.preventDefault();
    const modelVal = effectiveModelFromForm(modalUseCustom, modalPresetId, modalCustomModel);
    const canUseRag = isOpenAiChatModelId(modelVal);
    if (!modalName.trim()) {
      setErr('El nombre es obligatorio.');
      return;
    }
    if (modalUseCustom && !modalCustomModel.trim()) {
      setErr('Indicá el ID del modelo propio o desmarcá “Modelo propio”.');
      return;
    }
    if (Object.keys(modalLimitErrors).length > 0) {
      setErr('Corregí los errores en los límites de mensajes antes de guardar.');
      return;
    }
    if (modalAudioOutputEnabled) {
      if (!modalElevenLabsVoiceId.trim()) {
        setErr('Voice ID de ElevenLabs obligatorio si el output de audio está activo.');
        return;
      }
      if (agentModal?.kind === 'create' && !modalElevenLabsApiKey.trim()) {
        setErr('API key de ElevenLabs obligatoria si el output de audio está activo.');
        return;
      }
      if (agentModal?.kind === 'edit' && !modalElevenLabsKeyConfigured && !modalElevenLabsApiKey.trim()) {
        setErr('API key de ElevenLabs obligatoria si el output de audio está activo.');
        return;
      }
    }
    setErr('');
    setSaved('');
    setModalBusy(true);
    try {
      const audioPayload = {
        audioOutputEnabled: modalAudioOutputEnabled,
        audioInputEnabled: modalAudioInputEnabled,
        showWhatsappCustomerWindow: modalShowWhatsappWindow,
        elevenLabsVoiceId: modalElevenLabsVoiceId.trim() || null,
        elevenLabsModelId: modalElevenLabsModelId.trim() || null,
        ...(modalElevenLabsApiKey.trim() ? { elevenLabsApiKey: modalElevenLabsApiKey.trim() } : {}),
      };
      if (agentModal?.kind === 'create') {
        await apiPost(
          '/api/console/agents',
          {
            name: modalName.trim(),
            systemPrompt: modalPrompt.trim() || undefined,
            model: modelVal,
            useRag: canUseRag && modalUseRag,
            dailyMessageLimit: modalMessageLimit,
            extensionAmount: modalExtensionAmount,
            extensionValidityDays: modalValidityDays,
            ...audioPayload,
            ...(modalAudioOutputEnabled ? { elevenLabsApiKey: modalElevenLabsApiKey.trim() } : {}),
          },
          token,
          { successMessage: 'Agente creado' }
        );
        setSaved('Agente creado.');
      } else if (agentModal?.kind === 'edit') {
        await apiPatch(
          `/api/console/agents/${agentModal.agent.id}`,
          {
            name: modalName.trim(),
            systemPrompt: modalPrompt.trim() || null,
            model: modelVal,
            useRag: canUseRag && modalUseRag,
            dailyMessageLimit: modalMessageLimit,
            extensionAmount: modalExtensionAmount,
            extensionValidityDays: modalValidityDays,
            ...audioPayload,
          },
          token,
          { successMessage: 'Agente actualizado' }
        );
        setSaved('Agente actualizado.');
      }
      setAgentModal(null);
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setModalBusy(false);
    }
  };

  const toggle = async (a: Agent) => {
    setErr('');
    setSaved('');
    try {
      await apiPatch(`/api/console/agents/${a.id}`, { isActive: !a.isActive }, token, {
        successMessage: a.isActive ? 'Agente pausado' : 'Agente activado',
      });
      setSaved(a.isActive ? 'Agente pausado.' : 'Agente activado.');
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  const toggleDocsPanel = (agentId: string) => {
    setErr('');
    setDocsAgentId((cur) => (cur === agentId ? null : agentId));
  };

  const removeAgent = async (a: Agent) => {
    if (
      !confirm(
        `¿Eliminar el agente "${a.name}"? Esto borrará conversaciones, guía, atajos, documentos y demás datos relacionados.`
      )
    ) {
      return;
    }
    setErr('');
    setSaved('');
    try {
      await apiDelete(`/api/console/agents/${a.id}`, token, { successMessage: 'Agente eliminado' });
      setSaved('Agente eliminado.');
      setDocsAgentId((cur) => (cur === a.id ? null : cur));
      if (agentModal?.kind === 'edit' && agentModal.agent.id === a.id) {
        setAgentModal(null);
      }
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  const uploadFile = async (agentId: string, file: File | null) => {
    if (!file || !token) return;
    setErr('');
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await fetch(`/api/console/agents/${agentId}/context-documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const j = (await r.json()) as { success?: boolean; error?: { message?: string } };
      if (!r.ok || !j.success) {
        throw new Error(j.error?.message ?? `HTTP ${r.status}`);
      }
      showGrowl('Documento subido', 'success');
      void loadContextPanel(agentId);
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = async (agentId: string, docId: string) => {
    setErr('');
    try {
      await apiDelete(`/api/console/agents/${agentId}/context-documents/${docId}`, token, {
        successMessage: 'Documento eliminado',
      });
      void loadContextPanel(agentId);
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  const downloadDoc = async (agentId: string, doc: ContextDoc) => {
    if (!token) return;
    setErr('');
    try {
      const r = await fetch(`/api/console/agents/${agentId}/context-documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        let msg = `HTTP ${r.status}`;
        try {
          const j = (await r.json()) as { error?: { message?: string } };
          msg = j.error?.message ?? msg;
        } catch {
          // Ignorar body no JSON
        }
        throw new Error(msg);
      }
      const blob = await r.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = doc.originalFileName || 'documento';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  const addContextUrl = async (agentId: string) => {
    const u = urlInput.trim();
    if (!u || !token) return;
    setErr('');
    setUrlBusy(true);
    try {
      await apiPost(`/api/console/agents/${agentId}/context-urls`, { url: u }, token, {
        successMessage: 'URL de contexto añadida',
      });
      setUrlInput('');
      void loadContextPanel(agentId);
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setUrlBusy(false);
    }
  };

  const refreshContextUrl = async (agentId: string, urlRowId: string) => {
    setErr('');
    setUrlRefreshingId(urlRowId);
    try {
      await apiPost(`/api/console/agents/${agentId}/context-urls/${urlRowId}/refresh`, {}, token, {
        successMessage: 'Contenido de la URL actualizado',
      });
      void loadContextPanel(agentId);
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setUrlRefreshingId(null);
    }
  };

  const removeContextUrl = async (agentId: string, urlRowId: string) => {
    setErr('');
    try {
      await apiDelete(`/api/console/agents/${agentId}/context-urls/${urlRowId}`, token, {
        successMessage: 'URL eliminada',
      });
      void loadContextPanel(agentId);
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  const formatSize = (n: number) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  const openCreate = () => {
    setErr('');
    setSaved('');
    setAgentModal({ kind: 'create' });
  };

  const openEdit = (agent: Agent) => {
    setErr('');
    setSaved('');
    setAgentModal({ kind: 'edit', agent });
  };

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '28px 24px 40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ marginTop: 0, marginBottom: 4, fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>Agentes virtuales</h1>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>Configurá y gestioná los agentes que responden a tus visitantes.</p>
        </div>
        <button
          onClick={openCreate}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          + Crear agente
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 }}>Tus agentes</h2>
        <input
          type="search"
          placeholder={canWriteAgents ? 'Buscar por nombre, modelo, prompt…' : 'Buscar por nombre, prompt…'}
          value={agentQuery}
          onChange={(e) => setAgentQuery(e.target.value)}
          aria-label="Buscar agentes"
          style={{ ...inp, flex: '1 1 220px', maxWidth: 360, minWidth: 180, marginLeft: 'auto' }}
        />
        {canWriteAgents ?
          <button type="button" style={btnPrimary} onClick={openCreate}>
            Agregar agente
          </button>
        : null}
      </div>

      {saved ? <p style={{ color: '#166534', fontSize: 14, margin: '0 0 12px' }}>{saved}</p> : null}
      {err ? <p style={{ color: '#b91c1c', fontSize: 14, margin: '0 0 12px' }}>{err}</p> : null}

      {sortedRows.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: 14 }}>
          {canWriteAgents ? 'Todavía no hay agentes. Usá “Agregar agente”.' : 'Todavía no hay agentes.'}
        </p>
      ) : filteredRows.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: 14 }}>
          Ningún agente coincide con la búsqueda. Probá otro término o borrá el filtro.
        </p>
      ) : (
        <>
          <div style={{ overflowX: 'auto', borderRadius: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px', fontSize: 13 }}>
              <thead style={{ display: 'none' }}>
                <tr>
                  <th>Nombre</th>
                  <th>Modelo</th>
                  <th>Estado</th>
                  <th>Prompt</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {tableSlice.map((a) => (
                  <Fragment key={a.id}>
                    <tr style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                      <td style={{ padding: '14px 16px', fontWeight: 700, color: '#0f172a', borderTopLeftRadius: 10, borderBottomLeftRadius: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: a.isActive ? '#f5f3ff' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={a.isActive ? '#7c3aed' : '#94a3b8'} strokeWidth="2"><rect x="5" y="8" width="14" height="11" rx="3" /><path d="M9 8V6a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                          </div>
                          {a.name}
                        </div>
                      </td>
                      {canWriteAgents ?
                        <td
                          style={{
                            padding: '12px 14px',
                            fontFamily: 'ui-monospace, monospace',
                            fontSize: 12,
                            color: '#334155',
                          }}
                        >
                          {a.model}
                        </td>
                      : null}
                      <td style={{ padding: '12px 14px' }}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '3px 9px',
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 600,
                            background: a.isActive ? 'rgba(22,163,74,0.08)' : '#f1f5f9',
                            color: a.isActive ? '#16a34a' : '#64748b',
                          }}
                        >
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: a.isActive ? '#22c55e' : '#94a3b8' }} />
                          {a.isActive ? 'Activo' : 'Pausado'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', color: '#64748b', maxWidth: 280 }}>
                        {a.systemPrompt ? (
                          <span title={a.systemPrompt}>
                            {a.systemPrompt.length > 80 ? `${a.systemPrompt.slice(0, 80)}…` : a.systemPrompt}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle', textAlign: 'right' }}>
                        <div
                          style={{
                            display: 'inline-flex',
                            flexWrap: 'nowrap',
                            gap: 4,
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                          }}
                        >
                          {canWriteAgents ?
                            <button
                              type="button"
                              title="Editar agente"
                              aria-label="Editar agente"
                              style={btnIconNeutral}
                              onClick={() => openEdit(a)}
                            >
                              <IconPencil />
                            </button>
                          : null}
                          {canWriteAgents ?
                            <button
                              type="button"
                              title={a.isActive ? 'Pausar agente' : 'Activar agente'}
                              aria-label={a.isActive ? 'Pausar agente' : 'Activar agente'}
                              style={btnIconMuted}
                              onClick={() => void toggle(a)}
                            >
                              {a.isActive ? <IconPause /> : <IconPlay />}
                            </button>
                          : null}
                          <button
                            type="button"
                            title={
                              docsAgentId === a.id
                                ? 'Ocultar contexto, URLs y ejemplos'
                                : 'Contexto: archivos, URLs y ejemplos de respuesta'
                            }
                            aria-label={
                              docsAgentId === a.id
                                ? 'Ocultar contexto, URLs y ejemplos'
                                : 'Contexto: archivos, URLs y ejemplos de respuesta'
                            }
                            style={docsAgentId === a.id ? btnIconAccent : btnIconNeutral}
                            onClick={() => toggleDocsPanel(a.id)}
                          >
                            <IconFileStack />
                          </button>
                          <Link
                            to={`/app/agents/${a.id}/plugins`}
                            title="Gestionar plugins"
                            aria-label="Gestionar plugins"
                            style={btnIconNeutral}
                          >
                            <IconPlugins />
                          </Link>
                          {canManageAgents ?
                            <button
                              type="button"
                              title="Eliminar agente y datos relacionados"
                              aria-label="Eliminar agente y datos relacionados"
                              style={btnIconDanger}
                              onClick={() => void removeAgent(a)}
                            >
                              <IconTrash />
                            </button>
                          : null}
                        </div>
                      </td>
                    </tr>
                    {docsAgentId === a.id ? (
                      <tr key={`${a.id}-docs`}>
                        <td colSpan={canWriteAgents ? 5 : 4} style={{ padding: '0 14px 16px', background: '#fafafa', borderTop: 'none' }}>
                          <div style={{ paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
                            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 10px' }}>
                              <strong>Archivos y URLs</strong> aportan conocimiento. <strong>Ejemplos abajo</strong>{' '}
                              afinan tono y forma de respuesta (pares pregunta/respuesta), sin reentrenar al modelo.
                            </p>
                            <label style={{ display: 'inline-block', marginBottom: 12 }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: '#334155', marginRight: 8 }}>
                                Adjuntar archivo
                              </span>
                              <input
                                type="file"
                                accept=".pdf,.docx,.txt,.md,.csv,image/png,image/jpeg,image/webp,image/gif"
                                disabled={uploading}
                                onChange={(e) => {
                                  const f = e.target.files?.[0] ?? null;
                                  e.target.value = '';
                                  void uploadFile(a.id, f);
                                }}
                              />
                            </label>
                            {uploading ? <p style={{ fontSize: 13, color: '#64748b' }}>Subiendo…</p> : null}
                            {docsLoading ? (
                              <p style={{ fontSize: 13, color: '#64748b' }}>Cargando lista…</p>
                            ) : contextDocs.length === 0 ? (
                              <p style={{ fontSize: 13, color: '#94a3b8' }}>No hay archivos adjuntos.</p>
                            ) : (
                              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {contextDocs.map((d) => (
                                  <li
                                    key={d.id}
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      gap: 8,
                                      padding: '8px 0',
                                      borderBottom: '1px solid #f1f5f9',
                                      fontSize: 13,
                                    }}
                                  >
                                    <span>
                                      <strong style={{ color: '#334155' }}>{d.originalFileName}</strong>
                                      <span style={{ color: '#94a3b8', marginLeft: 8 }}>
                                        {formatSize(d.sizeBytes)} · {d.mimeType.split('/')[1] ?? d.mimeType}
                                      </span>
                                      <span style={{ color: '#cbd5e1', marginLeft: 8 }}>
                                        {new Date(d.createdAt).toLocaleString()}
                                      </span>
                                    </span>
                                    <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                      <button type="button" style={btnTable} onClick={() => void downloadDoc(a.id, d)}>
                                        Descargar
                                      </button>
                                      <button type="button" style={btnDanger} onClick={() => void removeDoc(a.id, d.id)}>
                                        Quitar
                                      </button>
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            <div
                              style={{
                                marginTop: 18,
                                paddingTop: 16,
                                borderTop: '1px solid #e2e8f0',
                              }}
                            >
                              <p style={{ fontSize: 13, fontWeight: 600, color: '#334155', margin: '0 0 8px' }}>
                                Páginas web de referencia
                              </p>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                <input
                                  type="url"
                                  style={{ ...inp, flex: '1 1 200px', minWidth: 160, maxWidth: 420 }}
                                  placeholder="https://ejemplo.com/docs"
                                  value={urlInput}
                                  disabled={urlBusy}
                                  onChange={(e) => setUrlInput(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      void addContextUrl(a.id);
                                    }
                                  }}
                                />
                                <button
                                  type="button"
                                  style={btnTable}
                                  disabled={urlBusy || !urlInput.trim()}
                                  onClick={() => void addContextUrl(a.id)}
                                >
                                  {urlBusy ? 'Descargando…' : 'Añadir URL'}
                                </button>
                              </div>
                              {docsLoading ? null : contextUrls.length === 0 ? (
                                <p style={{ fontSize: 13, color: '#94a3b8', margin: '10px 0 0' }}>
                                  No hay URLs. Solo <strong>https</strong>; redes privadas bloqueadas.
                                </p>
                              ) : (
                                <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 0' }}>
                                  {contextUrls.map((row) => (
                                    <li
                                      key={row.id}
                                      style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 8,
                                        padding: '8px 0',
                                        borderBottom: '1px solid #f1f5f9',
                                        fontSize: 13,
                                      }}
                                    >
                                      <span style={{ minWidth: 0 }}>
                                        <a
                                          href={row.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ color: '#2563eb', wordBreak: 'break-all' }}
                                        >
                                          {row.url}
                                        </a>
                                        <span style={{ color: '#94a3b8', marginLeft: 8 }}>
                                          {row.hasContent
                                            ? `${row.textChars.toLocaleString('es-UY')} caracteres`
                                            : 'sin texto'}
                                        </span>
                                        {row.lastFetchedAt ? (
                                          <span style={{ color: '#cbd5e1', marginLeft: 8 }}>
                                            · {new Date(row.lastFetchedAt).toLocaleString()}
                                          </span>
                                        ) : null}
                                        {row.fetchError ? (
                                          <span
                                            style={{ display: 'block', color: '#b91c1c', fontSize: 12, marginTop: 4 }}
                                          >
                                            {row.fetchError}
                                          </span>
                                        ) : null}
                                      </span>
                                      <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                        <button
                                          type="button"
                                          style={btnTable}
                                          disabled={urlRefreshingId === row.id}
                                          onClick={() => void refreshContextUrl(a.id, row.id)}
                                        >
                                          {urlRefreshingId === row.id ? '…' : 'Actualizar'}
                                        </button>
                                        <button
                                          type="button"
                                          style={btnDanger}
                                          onClick={() => void removeContextUrl(a.id, row.id)}
                                        >
                                          Quitar
                                        </button>
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 12,
              fontSize: 13,
              color: '#64748b',
            }}
          >
            <span>
              Página {tablePage} de {tableTotalPages} · {filteredRows.length} agente(s)
              {agentQuery.trim() ? ` (de ${sortedRows.length} en total)` : ''}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                disabled={tablePage <= 1}
                style={btnTable}
                onClick={() => setTablePage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>
              <button
                type="button"
                disabled={tablePage >= tableTotalPages}
                style={btnTable}
                onClick={() => setTablePage((p) => Math.min(tableTotalPages, p + 1))}
              >
                Siguiente
              </button>
            </div>
          </div>
        </>
      )}

      {agentModal ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="va-agent-modal-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            boxSizing: 'border-box',
          }}
          onClick={() => !modalBusy && setAgentModal(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              maxWidth: 520,
              width: '100%',
              maxHeight: 'min(92vh, 880px)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 24px 48px rgba(15, 23, 42, 0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <h2 id="va-agent-modal-title" style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                {agentModal.kind === 'create' ? 'Nuevo agente' : `Editar · ${agentModal.agent.name}`}
              </h2>
              <button
                type="button"
                aria-label="Cerrar"
                disabled={modalBusy}
                onClick={() => setAgentModal(null)}
                style={{
                  border: 'none',
                  background: '#f1f5f9',
                  borderRadius: 8,
                  width: 36,
                  height: 36,
                  cursor: modalBusy ? 'default' : 'pointer',
                  fontSize: 18,
                  lineHeight: 1,
                  color: '#475569',
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={(e) => void submitModal(e)} style={{ padding: 20, overflowY: 'auto', flex: 1, minHeight: 0 }}>
              <label style={lab}>
                Nombre
                <input
                  style={inp}
                  value={modalName}
                  onChange={(e) => setModalName(e.target.value)}
                  required
                  autoComplete="off"
                />
              </label>
              <div style={{ marginTop: 16 }}>
                {canWriteAgents ?
                  <ModelPicker
                    presets={presets}
                    useCustom={modalUseCustom}
                    onUseCustomChange={setModalUseCustom}
                    presetId={modalPresetId}
                    onPresetIdChange={setModalPresetId}
                    customText={modalCustomModel}
                    onCustomTextChange={setModalCustomModel}
                  />
                : null}
              </div>
              {showRagOption ? (
                <label
                  style={{
                    ...lab,
                    marginTop: 16,
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: 10,
                    cursor: 'pointer',
                    fontWeight: 500,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={modalUseRag}
                    onChange={(e) => setModalUseRag(e.target.checked)}
                    style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0 }}
                  />
                  <span>
                    Usar RAG para contexto (documentos y URLs)
                    <span style={{ display: 'block', fontSize: 12, color: '#64748b', fontWeight: 400, marginTop: 4 }}>
                      Requiere clave OpenAI para embeddings. El modelo de arriba sigue siendo el que responde; RAG solo
                      enriquece el contexto recuperado.
                    </span>
                  </span>
                </label>
              ) : (
                <p
                  style={{
                    margin: '16px 0 0',
                    fontSize: 12,
                    color: '#94a3b8',
                    lineHeight: 1.5,
                  }}
                >
                  RAG (contexto semántico con documentos/URLs) solo se configura con un <strong>modelo de chat OpenAI</strong>
                  (p. ej. <code style={{ fontSize: 11 }}>gpt-4o-mini</code>, <code style={{ fontSize: 11 }}>o1</code>). Con
                  Claude, DeepSeek u otro ID que no sea de la API de OpenAI, el contexto sigue en modo clásico.
                </p>
              )}
              <label style={{ ...lab, marginTop: 16 }}>
                Prompt de sistema (opcional)
                <textarea
                  style={{ ...inp, width: '100%', minHeight: 120, resize: 'vertical' }}
                  value={modalPrompt}
                  onChange={(e) => setModalPrompt(e.target.value)}
                />
              </label>
              <div
                style={{
                  marginTop: 20,
                  padding: 14,
                  borderRadius: 10,
                  border: '1px solid #e2e8f0',
                  background: '#f8fafc',
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    cursor: 'pointer',
                    fontWeight: 600,
                    color: '#0f172a',
                    fontSize: 14,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={modalAudioInputEnabled}
                    onChange={(e) => setModalAudioInputEnabled(e.target.checked)}
                    style={{ marginTop: 2, width: 18, height: 18, flexShrink: 0 }}
                  />
                  <span>
                    Entrada de audio (micrófono)
                    <span style={{ display: 'block', fontSize: 12, color: '#64748b', fontWeight: 400, marginTop: 4 }}>
                      Permite que el visitante envíe notas de voz en la demo y el widget. Requiere transcripción activa en la organización.
                    </span>
                  </span>
                </label>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    cursor: 'pointer',
                    fontWeight: 600,
                    color: '#0f172a',
                    fontSize: 14,
                    marginTop: 14,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={modalAudioOutputEnabled}
                    onChange={(e) => setModalAudioOutputEnabled(e.target.checked)}
                    style={{ marginTop: 2, width: 18, height: 18, flexShrink: 0 }}
                  />
                  <span>
                    Output de audio (ElevenLabs)
                    <span style={{ display: 'block', fontSize: 12, color: '#64748b', fontWeight: 400, marginTop: 4 }}>
                      Genera audio de la respuesta del agente (texto + audio) con la API key y voz de este agente.
                    </span>
                  </span>
                </label>
                {modalAudioOutputEnabled ? (
                  <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <label style={lab}>
                      API key de ElevenLabs
                      <input
                        style={inp}
                        type="password"
                        autoComplete="new-password"
                        value={modalElevenLabsApiKey}
                        onChange={(e) => setModalElevenLabsApiKey(e.target.value)}
                        placeholder={
                          modalElevenLabsKeyConfigured
                            ? `Configurada ${modalElevenLabsKeyHint ?? '••••'}`
                            : 'xi-...'
                        }
                      />
                      {modalElevenLabsKeyConfigured && !modalElevenLabsApiKey ? (
                        <span style={{ display: 'block', fontSize: 12, color: '#64748b', marginTop: 4 }}>
                          Dejá vacío para mantener la key actual.
                        </span>
                      ) : null}
                    </label>
                    <label style={lab}>
                      Voice ID
                      <input
                        style={inp}
                        value={modalElevenLabsVoiceId}
                        onChange={(e) => setModalElevenLabsVoiceId(e.target.value)}
                        placeholder="p. ej. 21m00Tcm4TlvDq8ikWAM"
                        autoComplete="off"
                      />
                    </label>
                    <label style={lab}>
                      Model ID (opcional)
                      <input
                        style={inp}
                        value={modalElevenLabsModelId}
                        onChange={(e) => setModalElevenLabsModelId(e.target.value)}
                        placeholder="eleven_turbo_v2_5"
                        autoComplete="off"
                      />
                      <span style={{ display: 'block', fontSize: 12, color: '#64748b', marginTop: 4 }}>
                        Por defecto se usa un modelo que acepta language_code (idioma de la conversación).
                      </span>
                    </label>
                  </div>
                ) : null}
              </div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  cursor: 'pointer',
                  fontWeight: 600,
                  color: '#0f172a',
                  fontSize: 14,
                  marginTop: 16,
                  padding: 14,
                  borderRadius: 10,
                  border: '1px solid #e2e8f0',
                  background: '#f8fafc',
                }}
              >
                <input
                  type="checkbox"
                  checked={modalShowWhatsappWindow}
                  onChange={(e) => setModalShowWhatsappWindow(e.target.checked)}
                  style={{ marginTop: 2, width: 18, height: 18, flexShrink: 0 }}
                />
                <span>
                  Mostrar ventana 24 h de WhatsApp en la PWA
                  <span style={{ display: 'block', fontSize: 12, color: '#64748b', fontWeight: 400, marginTop: 4 }}>
                    En chats de WhatsApp muestra cuánto tiempo resta para responder con texto libre. El bloqueo y el
                    envío de plantilla se configuran en Plantillas.
                  </span>
                </span>
              </label>
              <div style={{ marginTop: 20 }}>
                <DemoMessageLimitsSection
                  messageLimit={modalMessageLimit}
                  validityDays={modalValidityDays}
                  extensionAmount={modalExtensionAmount}
                  onChange={handleLimitChange}
                  errors={modalLimitErrors}
                />
              </div>
              <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" style={btnTable} disabled={modalBusy} onClick={() => setAgentModal(null)}>
                  Cancelar
                </button>
                <button type="submit" style={btnPrimary} disabled={modalBusy}>
                  {modalBusy ? 'Guardando…' : agentModal.kind === 'create' ? 'Crear agente' : 'Guardar cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const lab: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  fontSize: 13,
  fontWeight: 600,
  color: '#475569',
  gap: 4,
};
const inp: CSSProperties = {
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  fontSize: 14,
};
const btnPrimary: CSSProperties = {
  padding: '10px 16px',
  borderRadius: 8,
  border: 'none',
  background: '#7c3aed',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
};
const btnTable: CSSProperties = {
  padding: '6px 12px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#334155',
  fontWeight: 600,
  fontSize: 12,
  cursor: 'pointer',
};

const btnIconBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 36,
  padding: 0,
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#334155',
  cursor: 'pointer',
  flexShrink: 0,
};

const btnIconNeutral: CSSProperties = {
  ...btnIconBase,
};

const btnIconMuted: CSSProperties = {
  ...btnIconBase,
  background: '#f8fafc',
  color: '#475569',
};

const btnIconAccent: CSSProperties = {
  ...btnIconBase,
  background: '#f5f3ff',
  borderColor: '#c4b5fd',
  color: '#6d28d9',
};

const btnIconDanger: CSSProperties = {
  ...btnIconBase,
  background: '#fef2f2',
  borderColor: '#fecaca',
  color: '#b91c1c',
};

const btnDanger: CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid #fecaca',
  background: '#fff',
  color: '#b91c1c',
  fontSize: 12,
  cursor: 'pointer',
  flexShrink: 0,
};
